/*
  - This component will extend any valid additional attribute
  <Button
    href=""         // if href is sets, will render simple anchor tag
    to=""           // if to is set, will render Link component that will work with routers. else will render button tag
    variant=""      // "secondary" or "primary"
    rounded         // Boolean, with rouned edges
    icon={}         // Boolean; if button has only icon.   eg: {true}, {false}
    outline         // Boolean String value,            eg: "true", "false"
    disabled        // Boolean;                        eg: {true}, {false}
    nofocus         // Boolean;   disable Focus shadow effect
  >
    <IconBeforeText />
    Text                  // If there is no children will not render the component
    <IconAfterText />
  </Button>
*/
import React from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

// Main component
const Button = (props) => {
  if (!props.children) return "";
  if (props.href) return <AnchorStyle {...props}>{props.children}</AnchorStyle>;
  if (props.to) return <LinkStyle {...props} to={props.to || ""}></LinkStyle>;
  return <ButtonStyle {...props}>{props.children}</ButtonStyle>;
};
export default Button;

// Styling
const baseBtn = css`
  /* Commune Styling */
  min-width: 64px;
  max-width: 100%;
  line-height: 1;
  font-size: 1rem;
  font-weight: 400;
  display: inline-flex;
  text-decoration: none;
  text-align: center;
  justify-content: center;
  vertical-align: middle;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background: none;
  text-transform: capitalize;
  user-select: none;
  white-space: nowrap;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 13px 16px;
  background-color: transparent;
  color: ${({ theme }) => theme.color.primary};
  cursor: pointer;
  align-items: center;

  /* Stats */
  :hover {
    color: #3850b6;
    svg {
      & > * {
        fill: #3850b6;
      }
    }
  }
  ${({ nofocus }) =>
    !nofocus &&
    `
      &:focus {
        box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.5);
      }
    `}

  :visited {
    color: ${({ theme }) => theme.color.primary};
  }
  ${({ notification, theme }) =>
    notification &&
    `
    position: relative;
    ::after {
      content: "";
      position: absolute;
      width: 6px;
      height: 6px;
      background-color: ${theme.color.danger};
      border-radius: 50px;
      top: 18%;
      right: 22%;
    }
  `}
  /* Icons inside */
  svg {
    width: 15px;
    min-width: 15px;
    max-height: 13px;
    &:nth-child(1) {
      margin-right: 5px;
    }
    &:nth-child(2) {
      margin-left: 5px;
    }
    & > * {
      fill: ${({ theme }) => theme.color.primary};
    }
  }

  /* If there is only Icon */
  ${({ icon, theme }) =>
    icon &&
    `
      padding: 8px;
      min-width: auto;
      border-radius: 50px;
      :focus{
        box-shadow: 0 0 0 0.15rem rgba(69, 105, 255, 0.5);
      }
      svg{
        max-width: 15px;
        max-height: 15px;
        margin-right: 0 !important;
        margin-left: 0 !important;
        path{
          fill: ${theme.color.primaryText};
        }
      }
      &:hover{
        background-color: ${theme.color.secondaryBg};
        svg{
          path{
            fill: ${theme.color.primaryText};
          }
        }
      }
    `}

  /* Variants */
  ${({ variant, theme, link }) =>
    !variant &&
    link &&
    `
      padding: 0;
      transition: color 100ms;
      color: ${theme.color.primary};
      :hover {
        transition: color 200ms;
        color: ${theme.color.darkerText};
        filter: brightness(1.5);
      }
      &:focus {
        color: ${theme.color.darkerText} !important;
        box-shadow: none;
      }
    `}
  ${({ variant }) => variant === "secondary" && secondaryBtn}
  ${({ variant }) => variant === "primary" && primaryBtn}
  ${({ variant }) => variant === "danger" && dangerBtn}
  ${({ outline }) => outline && outlineBtn}
  ${({ rounded }) => rounded && "border-radius: 50px;"}
  ${({ disabled }) => disabled && disabledBtn}

  /* Sizes */
  ${({ size }) =>
    size === "large" &&
    css`
      padding-top: 17px;
      padding-bottom: 17px;
    `}
  ${({ size }) =>
    size === "small" &&
    css`
      padding-top: 9px;
      padding-bottom: 9px;
      font-size: 13px;
    `}
`;
const secondaryBtn = css`
  background-color: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.primaryText} !important;
  &:hover,
  &:focus {
    color: ${({ theme }) => theme.color.white} !important;
    background-color: ${({ theme }) => theme.color.primary};
    svg {
      & > * {
        fill: ${({ theme }) => theme.color.white} !important;
      }
    }
  }
  &:active {
    background-color: #213381;
    color: ${({ theme }) => theme.color.white};
  }

  svg {
    & > * {
      fill: ${({ theme }) => theme.color.primaryText};
    }
  }
`;
const primaryBtn = css`
  background-color: ${({ theme }) => theme.color.primary};
  color: ${({ theme }) => theme.color.white} !important;

  &:hover {
    background-color: #3850b6;
    color: ${({ theme }) => theme.color.white};
    svg {
      & > * {
        fill: ${({ theme }) => theme.color.white};
      }
    }
  }
  &:active {
    background-color: #213381;
    color: ${({ theme }) => theme.color.white};
  }

  svg {
    & > * {
      fill: ${({ theme }) => theme.color.white};
    }
  }
`;
const dangerBtn = css`
  background-color: ${({ theme }) => theme.color.danger};
  color: ${({ theme }) => theme.color.white} !important;

  :hover {
    background-color: rgba(238, 76, 80, 0.9);
    color: ${({ theme }) => theme.color.white};
    svg {
      & > * {
        fill: ${({ theme }) => theme.color.white};
      }
    }
  }
  :active {
    background-color: ${({ theme }) => theme.color.danger};
    color: ${({ theme }) => theme.color.white};
  }
  :focus {
    box-shadow: 0 0 0 0.2rem rgba(238, 76, 80, 0.5);
  }
  svg {
    & > * {
      fill: ${({ theme }) => theme.color.white};
    }
  }
`;
const outlineBtn = css`
  border: 1px solid ${({ theme }) => theme.color.primary};
  &:hover {
    background-color: ${({ theme }) => theme.color.primary};
    color: ${({ theme }) => theme.color.white};
    svg {
      & > * {
        fill: ${({ theme }) => theme.color.white};
      }
    }
  }
  svg {
    & > * {
      fill: ${({ theme }) => theme.color.primary};
    }
  }
`;
const disabledBtn = css`
  transition: color 0ms, background-color 0ms;
  /* Override variant colors (some variants set color: ... !important) */
  color: ${({ theme }) => theme.color.lighterText} !important;
  background-color: ${({ theme }) => theme.color.secondaryBg} !important;
  border-color: ${({ theme }) => theme.color.border} !important;
  pointer-events: none;

  &:hover,
  &:active,
  &:visited,
  &:focus {
    color: ${({ theme }) => theme.color.lighterText} !important;
    background-color: ${({ theme }) => theme.color.secondaryBg} !important;
    box-shadow: none;
    outline: none;
  }
  svg {
    & > * {
      fill: ${({ theme }) => theme.color.lighterText} !important;
    }
  }
`;
const ButtonStyle = styled.button`
  ${baseBtn}
`;
const LinkStyle = styled(Link)`
  ${baseBtn}
`;
const AnchorStyle = styled.a`
  ${baseBtn}
`;
